import { Box, useMediaQuery } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import gaiaTheme from '../../../../root-components/theme';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { IEnergySupplierTariff, IPostEnergySupplierTariff } from '../../../../shared/services/energy-supplier-tariff/energy-supplier-tariff.model';
import EnergySupplierService from '../../../../shared/services/energy-supplier/energy-supplier.service';
import { IEnergySupplier } from '../../../../shared/services/energy-supplier/energy-supplier.model';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { EnergySupplierTariffDialog } from '../../../dialogs/super-admin/energy-supplier-tariff-dialog';
import EnergySupplierTariffService from '../../../../shared/services/energy-supplier-tariff/energy-supplier-tariff.service';

export const SuperAdminEnergySupplierPage = () => {
  const { id } = useParams()
  const navigate = useNavigate();
  //Services
  const energySupplierService = new EnergySupplierService()
  const energySupplierTariffService = new EnergySupplierTariffService()
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [energySupplier, setEnergySupplier] = useState<IEnergySupplier>()
  const [energySupplerTariffs, setEnergySupplierTariffs] = useState<IEnergySupplierTariff[]>()
  const [searchQuery, setSearchQuery] = useState('')
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Dialog State
  const [isTariffDialogOpen, setIsTariffDialogOpen] = useState<boolean>(false)
  const [selectedTariffId, setSelectedTariffId] = useState<string>('')
  //Loading State
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  //Use Effects
  useEffect(() => {
    if (id) {
      energySupplierService.getOne(id)
        .then(response => {
          setEnergySupplier(response)
        })
        .catch(err => console.log(err))
    }
    getAllTariffs()
  }, [inputPage, perPage, change, id])

  const getAllTariffs = () => {
    energySupplierTariffService.getAllWithPagination(searchQuery, inputPage, perPage, orderBy, order, id)
      .then(response => {
        setIsLoading(false)
        setTotalRecords(response.count);
        setEnergySupplierTariffs(response.rows)
      })
      .catch(err => {
        console.log(err)
      })
  }

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy, searchQuery])

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true },
    { id: 'active', numeric: false, label: 'Status', sortable: true },
  ];

  const rows = !energySupplerTariffs ? [] : energySupplerTariffs.map(tariff => {
    return {
      id: tariff.id,
      action: (
        <TableActionButtons
          id={tariff.id}
          includeEdit={true}
          includeView={true}
          includeDeactivate={tariff.active}
          onDeactivate={() => deactivateHandler(tariff.id)}
          includeOther={!tariff.active && tariff.hasTariffDetails}
          otherButtonIcon={IconName.Autorenew}
          otherButtonText={'Activate'}
          onOther={() => activateHandler(tariff.id)}
          onEdit={() => editHandler(tariff.id)}
          onView={() => navigate(`/super-admin/energy-suppliers/${id}/${tariff.id}`)}
        />
      ),
      name: tariff.name,
      active: tariff.active ? 'Active' : 'Inactive',
    }
  });

  // Table Event Handlers
  const editHandler = (tariffId: string) => {
    setSelectedTariffId(tariffId)
    setIsTariffDialogOpen(true)
  }

  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const deactivateHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `By deactivating this tariff, users will not be able to use this tariff for emissions.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response) {
          energySupplierTariffService.deactivate(id)
            .then(response => {
              getAllTariffs()
              userNotificationsContext.openSnackbar({ open: true, message: 'Deactivated successfully' });
            })
            .catch(err => {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              console.log(err)
            })
        }
      })
  }

  const activateHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `By activating this tariff, users will be able to use this tariff for emissions.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response) {
          energySupplierTariffService.activate(id)
            .then(response => {
              getAllTariffs()
              userNotificationsContext.openSnackbar({ open: true, message: 'Activated successfully' });
            })
            .catch(err => {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              console.log(err)
            })
        }
      })
  }


  const submitHandler = async (newTarrif: IPostEnergySupplierTariff, isEdit: boolean, resetForm: () => void, energySupplierTariffId?: string,) => {
    if (isEdit && energySupplierTariffId) {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        energySupplierTariffService.edit(energySupplierTariffId, newTarrif)
          .then(response => {
            getAllTariffs()
            resetForm()
            setIsTariffDialogOpen(false)
            setSelectedTariffId('')
            userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
            setIsDialogSubmitting(false)
          })
          .catch(err => {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            setIsDialogSubmitting(false)
            console.log(err)
          })
      } else {
        userNotificationsContext.openSnackbar({ open: true, message: 'Saving in progress, please wait' });
      }
    } else {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        const tariffWithSupplierId = { ...newTarrif, energySupplierId: id! }
        energySupplierTariffService.create(tariffWithSupplierId)
          .then(response => {
            setIsDialogSubmitting(false)
            setIsTariffDialogOpen(false)
            userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
            getAllTariffs()
            resetForm()
          })
          .catch(err => {
            setIsDialogSubmitting(false)
            console.log(err)
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });

          })
      } else {
        userNotificationsContext.openSnackbar({ open: true, message: 'Saving in progress, please wait' });
      }
    }
  }


  return (
    <PageOuterContainer>
      {energySupplier &&
        <PageHeading
          title={energySupplier.name}
          includeBackButton={true}
          backButtonLink='/super-admin/energy-suppliers'
          backButtonText={`Back to energy suppliers`}
          includeActionButton={true}
          actionButtonIcon={IconName.Add}
          actionButtonHandler={() => { setIsTariffDialogOpen(true) }}
          actionButtonText={`Add Tariff`}
        />}
      <Box sx={{ width: '100%' }}>
        {/* <Grid container sx={{ mb: 2 }}>
          <Grid
            item
            xl={4} lg={4} md={6} sm={12} xs={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              width: '100%'
            }}>
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </Grid>
        </Grid> */}
        <LoadingPage pageReady={!isLoading}>
          {energySupplerTariffs &&
            <TableDisplay
              onOrderChange={handleOrderChange}
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={rows}
              selectByIcon={true}
              selected={selected}
              setSelected={setSelected}
              inputPage={inputPage}
              setInputPage={setInputPage}
              totalRecords={totalRecords}
              perPage={perPage}
              setPerPage={setPerPage} />}
        </LoadingPage>
      </Box>
      {selectedTariffId && isTariffDialogOpen &&
        <EnergySupplierTariffDialog
          energySupplierTariffId={selectedTariffId}
          open={isTariffDialogOpen}
          closeHandler={() => {
            setIsTariffDialogOpen(false)
            setSelectedTariffId('')
          }}
          isEdit={true}
          isSubmitting={isDialogSubmitting}
          submitHandler={submitHandler}
        />}
      {!selectedTariffId && isTariffDialogOpen && id &&
        <EnergySupplierTariffDialog
          open={isTariffDialogOpen}
          closeHandler={() => setIsTariffDialogOpen(false)}
          isEdit={false}
          energySupplierId={id}
          isSubmitting={isDialogSubmitting}
          submitHandler={submitHandler}
        />}
    </PageOuterContainer>
  )
}